import React, { useState, useEffect } from 'react';
import { supportAPI } from '../../services/api';

const TICKET_CATEGORIES = [
  { value: 'Orders', label: 'Order Issues' },
  { value: 'Payments', label: 'Payment Problems' },
  { value: 'Account', label: 'Account & Profile' },
  { value: 'Technical', label: 'Technical Issues' },
  { value: 'Subscription', label: 'Subscription' },
  { value: 'Menu Management', label: 'Menu Management' },
  { value: 'Other', label: 'Other' }
];

const PRIORITY_OPTIONS = [
  { value: 'low', label: 'Low', color: '#10b981' },
  { value: 'medium', label: 'Medium', color: '#f59e0b' },
  { value: 'high', label: 'High', color: '#ef4444' }
];

const FAQ_ITEMS = [
  {
    question: 'How do I update my menu?',
    answer: 'Go to Menu Management from the sidebar or dashboard. You can add, edit, or remove items from there.'
  },
  {
    question: 'When will I receive my payout?',
    answer: 'Payouts are processed every week. You can check your earnings and payout schedule in the Earnings section.'
  },
  {
    question: 'How do I change my opening hours?',
    answer: 'Navigate to Opening Hours from your Profile settings to update your operating schedule.'
  },
  {
    question: 'Why is my restaurant showing as closed?',
    answer: 'Check if your restaurant is toggled open on the dashboard. Also verify your opening hours are set correctly.'
  },
  {
    question: 'How do I respond to customer reviews?',
    answer: 'Go to Reviews from your Profile settings. You can reply to customer reviews directly from there.'
  }
];

function HelpSupport() {
  const [activeTab, setActiveTab] = useState('faq');
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [expandedFaq, setExpandedFaq] = useState(null);

  // New ticket form
  const [newTicket, setNewTicket] = useState({
    subject: '',
    category: 'Orders',
    description: '',
    priority: 'medium'
  });
  const [submittingTicket, setSubmittingTicket] = useState(false);

  // Reply form
  const [replyMessage, setReplyMessage] = useState('');
  const [submittingReply, setSubmittingReply] = useState(false);

  useEffect(() => {
    if (activeTab === 'tickets') {
      fetchTickets();
    }
  }, [activeTab]);

  const fetchTickets = async () => {
    try {
      setLoading(true);
      const response = await supportAPI.getMyTickets();
      setTickets(response.data.data?.tickets || []);
    } catch (error) {
      console.error('Failed to fetch tickets:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTicket = async (e) => {
    e.preventDefault();
    if (!newTicket.subject.trim() || !newTicket.description.trim()) {
      alert('Please fill in all required fields');
      return;
    }

    try {
      setSubmittingTicket(true);
      await supportAPI.createTicket(newTicket);
      setNewTicket({ subject: '', category: 'Orders', description: '', priority: 'medium' });
      setActiveTab('tickets');
      fetchTickets();
      alert('Support ticket created successfully!');
    } catch (error) {
      console.error('Failed to create ticket:', error);
      alert('Failed to create ticket. Please try again.');
    } finally {
      setSubmittingTicket(false);
    }
  };

  const handleViewTicket = async (ticket) => {
    try {
      const response = await supportAPI.getTicketById(ticket.ticketId);
      setSelectedTicket(response.data.data?.ticket || ticket);
    } catch (error) {
      console.error('Failed to fetch ticket details:', error);
      setSelectedTicket(ticket);
    }
  };

  const handleReply = async () => {
    if (!replyMessage.trim()) return;

    try {
      setSubmittingReply(true);
      await supportAPI.addResponse(selectedTicket.ticketId, { message: replyMessage });
      setReplyMessage('');
      handleViewTicket(selectedTicket);
    } catch (error) {
      console.error('Failed to add reply:', error);
      alert('Failed to send reply. Please try again.');
    } finally {
      setSubmittingReply(false);
    }
  };

  const handleCloseTicket = async () => {
    if (!window.confirm('Are you sure you want to close this ticket?')) return;

    try {
      await supportAPI.closeTicket(selectedTicket.ticketId);
      setSelectedTicket(null);
      fetchTickets();
    } catch (error) {
      console.error('Failed to close ticket:', error);
      alert('Failed to close ticket. Please try again.');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      timeZone: 'Asia/Kolkata',
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    const styles = {
      open: { bg: 'rgba(16, 185, 129, 0.1)', color: '#059669' },
      pending: { bg: 'rgba(245, 158, 11, 0.1)', color: '#d97706' },
      closed: { bg: 'rgba(107, 114, 128, 0.1)', color: '#64748B' }
    };
    const style = styles[status] || styles.open;

    return (
      <span style={{
        padding: '6px 12px',
        borderRadius: '8px',
        fontSize: '12px',
        fontWeight: '600',
        background: style.bg,
        color: style.color,
        textTransform: 'capitalize'
      }}>
        {status}
      </span>
    );
  };

  return (
    <div style={{ background: '#F8FAFC', minHeight: '100vh' }}>
      <div className="container">
        {/* Header with Gradient */}
        <div style={{
          background: 'linear-gradient(135deg, #FF6961 0%, #FF8A80 100%)',
          borderRadius: '16px',
          padding: '40px 32px',
          textAlign: 'center',
          marginBottom: '32px',
          boxShadow: '0 4px 12px rgba(255, 105, 97, 0.15)'
        }} className="fade-in">
          <h1 style={{
            fontSize: '32px',
            fontWeight: '700',
            color: 'white',
            marginBottom: '8px',
            letterSpacing: '-0.5px'
          }}>
            💬 Help & Support
          </h1>
          <p style={{ color: 'rgba(255, 255, 255, 0.9)', fontSize: '16px', margin: 0 }}>
            Get help with any issues or questions
          </p>
        </div>

        {/* Tab Bar */}
        <div style={{
          display: 'flex',
          gap: '8px',
          marginBottom: '24px',
          background: 'white',
          padding: '8px',
          borderRadius: '12px',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)'
        }}>
          {[
            { id: 'faq', label: 'FAQ', icon: '❓' },
            { id: 'new', label: 'Create Ticket', icon: '✏️' },
            { id: 'tickets', label: 'My Tickets', icon: '📋' }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => {
                setActiveTab(tab.id);
                setSelectedTicket(null);
              }}
              style={{
                flex: 1,
                padding: '12px 16px',
                border: 'none',
                borderRadius: '8px',
                background: activeTab === tab.id
                  ? 'linear-gradient(135deg, #FF6961 0%, #FF8A80 100%)'
                  : 'transparent',
                color: activeTab === tab.id ? 'white' : '#64748B',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '6px',
                boxShadow: activeTab === tab.id ? '0 2px 8px rgba(255, 105, 97, 0.2)' : 'none'
              }}
              onMouseEnter={(e) => {
                if (activeTab !== tab.id) {
                  e.currentTarget.style.background = '#F8FAFC';
                }
              }}
              onMouseLeave={(e) => {
                if (activeTab !== tab.id) {
                  e.currentTarget.style.background = 'transparent';
                }
              }}
            >
              <span style={{ fontSize: '16px' }}>{tab.icon}</span>
              <span>{tab.label}</span>
            </button>
          ))}
        </div>

        {/* FAQ Tab */}
        {activeTab === 'faq' && (
          <div style={{
            background: 'white',
            borderRadius: '16px',
            padding: '24px',
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)'
          }} className="fade-in">
            <h3 style={{
              fontSize: '20px',
              fontWeight: '700',
              marginBottom: '20px',
              color: '#0F172A'
            }}>
              Frequently Asked Questions
            </h3>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {FAQ_ITEMS.map((faq, index) => (
                <div key={index} style={{
                  border: '1px solid #E2E8F0',
                  borderRadius: '12px',
                  overflow: 'hidden',
                  transition: 'all 0.2s ease'
                }}>
                  <button
                    onClick={() => setExpandedFaq(expandedFaq === index ? null : index)}
                    style={{
                      width: '100%',
                      padding: '16px 20px',
                      background: expandedFaq === index ? '#F8FAFC' : 'white',
                      border: 'none',
                      cursor: 'pointer',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      textAlign: 'left',
                      fontFamily: 'inherit',
                      transition: 'background 0.2s ease'
                    }}
                  >
                    <span style={{
                      fontWeight: '600',
                      color: '#0F172A',
                      fontSize: '15px'
                    }}>
                      {faq.question}
                    </span>
                    <span style={{
                      transform: expandedFaq === index ? 'rotate(180deg)' : 'rotate(0)',
                      transition: 'transform 0.2s ease',
                      color: '#FF6961',
                      fontSize: '12px'
                    }}>
                      ▼
                    </span>
                  </button>
                  {expandedFaq === index && (
                    <div style={{
                      padding: '16px 20px',
                      background: '#F8FAFC',
                      color: '#64748B',
                      lineHeight: '1.6',
                      fontSize: '14px',
                      borderTop: '1px solid #E2E8F0'
                    }}>
                      {faq.answer}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* New Ticket Tab */}
        {activeTab === 'new' && (
          <div style={{
            background: 'white',
            borderRadius: '16px',
            padding: '24px',
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)'
          }} className="fade-in">
            <h3 style={{
              fontSize: '20px',
              fontWeight: '700',
              marginBottom: '24px',
              color: '#0F172A'
            }}>
              Create Support Ticket
            </h3>

            <form onSubmit={handleCreateTicket}>
              <div style={{ marginBottom: '20px' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#0F172A'
                }}>
                  Subject *
                </label>
                <input
                  type="text"
                  value={newTicket.subject}
                  onChange={(e) => setNewTicket({ ...newTicket, subject: e.target.value })}
                  placeholder="Brief description of your issue"
                  required
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    fontSize: '14px',
                    border: '1px solid #E2E8F0',
                    borderRadius: '8px',
                    background: '#F8FAFC',
                    color: '#0F172A',
                    transition: 'all 0.2s ease',
                    outline: 'none'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#FF6961';
                    e.target.style.background = 'white';
                    e.target.style.boxShadow = '0 0 0 3px rgba(255, 105, 97, 0.1)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#E2E8F0';
                    e.target.style.background = '#F8FAFC';
                    e.target.style.boxShadow = 'none';
                  }}
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '20px' }}>
                <div>
                  <label style={{
                    display: 'block',
                    marginBottom: '8px',
                    fontSize: '14px',
                    fontWeight: '600',
                    color: '#0F172A'
                  }}>
                    Category
                  </label>
                  <select
                    value={newTicket.category}
                    onChange={(e) => setNewTicket({ ...newTicket, category: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      fontSize: '14px',
                      border: '1px solid #E2E8F0',
                      borderRadius: '8px',
                      background: '#F8FAFC',
                      color: '#0F172A',
                      transition: 'all 0.2s ease',
                      outline: 'none',
                      cursor: 'pointer'
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#FF6961';
                      e.target.style.background = 'white';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = '#E2E8F0';
                      e.target.style.background = '#F8FAFC';
                    }}
                  >
                    {TICKET_CATEGORIES.map(cat => (
                      <option key={cat.value} value={cat.value}>{cat.label}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label style={{
                    display: 'block',
                    marginBottom: '8px',
                    fontSize: '14px',
                    fontWeight: '600',
                    color: '#0F172A'
                  }}>
                    Priority
                  </label>
                  <select
                    value={newTicket.priority}
                    onChange={(e) => setNewTicket({ ...newTicket, priority: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      fontSize: '14px',
                      border: '1px solid #E2E8F0',
                      borderRadius: '8px',
                      background: '#F8FAFC',
                      color: '#0F172A',
                      transition: 'all 0.2s ease',
                      outline: 'none',
                      cursor: 'pointer'
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#FF6961';
                      e.target.style.background = 'white';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = '#E2E8F0';
                      e.target.style.background = '#F8FAFC';
                    }}
                  >
                    {PRIORITY_OPTIONS.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div style={{ marginBottom: '24px' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#0F172A'
                }}>
                  Description *
                </label>
                <textarea
                  value={newTicket.description}
                  onChange={(e) => setNewTicket({ ...newTicket, description: e.target.value })}
                  placeholder="Provide detailed information about your issue..."
                  rows={6}
                  required
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    fontSize: '14px',
                    border: '1px solid #E2E8F0',
                    borderRadius: '8px',
                    background: '#F8FAFC',
                    color: '#0F172A',
                    transition: 'all 0.2s ease',
                    outline: 'none',
                    resize: 'vertical',
                    fontFamily: 'inherit',
                    lineHeight: '1.6'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#FF6961';
                    e.target.style.background = 'white';
                    e.target.style.boxShadow = '0 0 0 3px rgba(255, 105, 97, 0.1)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#E2E8F0';
                    e.target.style.background = '#F8FAFC';
                    e.target.style.boxShadow = 'none';
                  }}
                />
              </div>

              <button
                type="submit"
                disabled={submittingTicket}
                style={{
                  width: '100%',
                  padding: '14px 24px',
                  background: submittingTicket
                    ? '#CBD5E1'
                    : 'linear-gradient(135deg, #FF6961 0%, #FF8A80 100%)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '16px',
                  fontWeight: '600',
                  cursor: submittingTicket ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  transition: 'all 0.2s ease',
                  boxShadow: submittingTicket ? 'none' : '0 2px 8px rgba(255, 105, 97, 0.2)'
                }}
                onMouseEnter={(e) => {
                  if (!submittingTicket) {
                    e.currentTarget.style.transform = 'translateY(-1px)';
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(255, 105, 97, 0.3)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!submittingTicket) {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 2px 8px rgba(255, 105, 97, 0.2)';
                  }
                }}
              >
                {submittingTicket ? (
                  <>
                    <span className="spinner" style={{
                      width: '20px',
                      height: '20px',
                      border: '2px solid rgba(255,255,255,0.3)',
                      borderTopColor: 'white',
                      borderRadius: '50%',
                      animation: 'spin 0.6s linear infinite'
                    }}></span>
                    <span>Creating...</span>
                  </>
                ) : (
                  <>
                    <span style={{ fontSize: '18px' }}>📨</span>
                    <span>Submit Ticket</span>
                  </>
                )}
              </button>
            </form>
          </div>
        )}

        {/* Tickets Tab */}
        {activeTab === 'tickets' && !selectedTicket && (
          <div style={{
            background: 'white',
            borderRadius: '16px',
            padding: '24px',
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)'
          }} className="fade-in">
            <h3 style={{
              fontSize: '20px',
              fontWeight: '700',
              marginBottom: '20px',
              color: '#0F172A'
            }}>
              My Support Tickets
            </h3>

            {loading ? (
              <div style={{
                textAlign: 'center',
                padding: '40px',
                color: '#64748B'
              }}>
                Loading tickets...
              </div>
            ) : tickets.length === 0 ? (
              <div style={{
                textAlign: 'center',
                padding: '60px 20px',
                color: '#64748B'
              }}>
                <div style={{ fontSize: '64px', marginBottom: '16px' }}>📭</div>
                <p style={{ margin: '0 0 20px 0', fontSize: '16px' }}>No support tickets yet</p>
                <button
                  onClick={() => setActiveTab('new')}
                  style={{
                    padding: '12px 24px',
                    background: 'linear-gradient(135deg, #FF6961 0%, #FF8A80 100%)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    boxShadow: '0 2px 8px rgba(255, 105, 97, 0.2)',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-1px)';
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(255, 105, 97, 0.3)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 2px 8px rgba(255, 105, 97, 0.2)';
                  }}
                >
                  Create New Ticket
                </button>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {tickets.map(ticket => (
                  <div
                    key={ticket._id}
                    onClick={() => handleViewTicket(ticket)}
                    style={{
                      padding: '16px',
                      background: '#F8FAFC',
                      borderRadius: '12px',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      border: '1px solid #E2E8F0'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = 'white';
                      e.currentTarget.style.borderColor = '#FF6961';
                      e.currentTarget.style.boxShadow = '0 2px 8px rgba(255, 105, 97, 0.1)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = '#F8FAFC';
                      e.currentTarget.style.borderColor = '#E2E8F0';
                      e.currentTarget.style.boxShadow = 'none';
                    }}
                  >
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      marginBottom: '8px'
                    }}>
                      <div style={{
                        fontWeight: '600',
                        color: '#0F172A',
                        fontSize: '15px'
                      }}>
                        {ticket.subject}
                      </div>
                      {getStatusBadge(ticket.status)}
                    </div>
                    <div style={{
                      display: 'flex',
                      gap: '16px',
                      fontSize: '13px',
                      color: '#64748B',
                      flexWrap: 'wrap'
                    }}>
                      <span>#{ticket.ticketId}</span>
                      <span>{TICKET_CATEGORIES.find(c => c.value === ticket.category)?.label}</span>
                      <span>{formatDate(ticket.createdAt)}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Ticket Detail View */}
        {activeTab === 'tickets' && selectedTicket && (
          <div style={{
            background: 'white',
            borderRadius: '16px',
            padding: '24px',
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)'
          }} className="fade-in">
            <button
              onClick={() => setSelectedTicket(null)}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                color: '#64748B',
                marginBottom: '20px',
                padding: '8px 12px',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: '500',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = '#F8FAFC';
                e.currentTarget.style.color = '#0F172A';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'none';
                e.currentTarget.style.color = '#64748B';
              }}
            >
              <span>←</span>
              <span>Back to Tickets</span>
            </button>

            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              marginBottom: '24px'
            }}>
              <div>
                <h3 style={{
                  fontSize: '20px',
                  fontWeight: '700',
                  color: '#0F172A',
                  marginBottom: '12px'
                }}>
                  {selectedTicket.subject}
                </h3>
                <div style={{
                  display: 'flex',
                  gap: '12px',
                  fontSize: '13px',
                  color: '#64748B',
                  alignItems: 'center',
                  flexWrap: 'wrap'
                }}>
                  <span>#{selectedTicket.ticketId}</span>
                  <span>{TICKET_CATEGORIES.find(c => c.value === selectedTicket.category)?.label}</span>
                  {getStatusBadge(selectedTicket.status)}
                </div>
              </div>
              {selectedTicket.status !== 'closed' && (
                <button
                  onClick={handleCloseTicket}
                  style={{
                    padding: '10px 16px',
                    background: '#F8FAFC',
                    color: '#64748B',
                    border: '1px solid #E2E8F0',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = '#EF4444';
                    e.currentTarget.style.color = 'white';
                    e.currentTarget.style.borderColor = '#EF4444';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = '#F8FAFC';
                    e.currentTarget.style.color = '#64748B';
                    e.currentTarget.style.borderColor = '#E2E8F0';
                  }}
                >
                  Close Ticket
                </button>
              )}
            </div>

            {/* Original Message */}
            <div style={{
              padding: '16px',
              background: '#F8FAFC',
              borderRadius: '12px',
              marginBottom: '20px',
              border: '1px solid #E2E8F0'
            }}>
              <div style={{
                fontSize: '13px',
                color: '#64748B',
                marginBottom: '8px'
              }}>
                {formatDate(selectedTicket.createdAt)}
              </div>
              <p style={{
                color: '#0F172A',
                margin: 0,
                lineHeight: '1.6',
                fontSize: '14px'
              }}>
                {selectedTicket.description}
              </p>
            </div>

            {/* Responses */}
            {selectedTicket.responses?.length > 0 && (
              <div style={{ marginBottom: '24px' }}>
                <h4 style={{
                  fontSize: '18px',
                  fontWeight: '600',
                  marginBottom: '16px',
                  color: '#0F172A'
                }}>
                  Conversation
                </h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {selectedTicket.responses.map((response, index) => (
                    <div
                      key={index}
                      style={{
                        padding: '16px',
                        background: response.isFromSupport ? 'rgba(255, 105, 97, 0.05)' : '#F8FAFC',
                        borderRadius: '12px',
                        borderLeft: response.isFromSupport ? '3px solid #FF6961' : '3px solid #CBD5E1',
                        border: '1px solid ' + (response.isFromSupport ? 'rgba(255, 105, 97, 0.1)' : '#E2E8F0')
                      }}
                    >
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        marginBottom: '8px',
                        fontSize: '13px',
                        alignItems: 'center',
                        flexWrap: 'wrap',
                        gap: '8px'
                      }}>
                        <span style={{
                          fontWeight: '600',
                          color: response.isFromSupport ? '#FF6961' : '#0F172A',
                          fontSize: '14px'
                        }}>
                          {response.isFromSupport ? '🛡️ Support Team' : '👤 You'}
                        </span>
                        <span style={{ color: '#64748B' }}>
                          {formatDate(response.createdAt)}
                        </span>
                      </div>
                      <p style={{
                        color: '#0F172A',
                        margin: 0,
                        lineHeight: '1.6',
                        fontSize: '14px'
                      }}>
                        {response.message}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Reply Form */}
            {selectedTicket.status !== 'closed' && (
              <div>
                <h4 style={{
                  fontSize: '18px',
                  fontWeight: '600',
                  marginBottom: '12px',
                  color: '#0F172A'
                }}>
                  Add Reply
                </h4>
                <textarea
                  value={replyMessage}
                  onChange={(e) => setReplyMessage(e.target.value)}
                  placeholder="Type your message..."
                  rows={4}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    fontSize: '14px',
                    border: '1px solid #E2E8F0',
                    borderRadius: '8px',
                    background: '#F8FAFC',
                    color: '#0F172A',
                    transition: 'all 0.2s ease',
                    outline: 'none',
                    resize: 'vertical',
                    fontFamily: 'inherit',
                    lineHeight: '1.6',
                    marginBottom: '16px'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#FF6961';
                    e.target.style.background = 'white';
                    e.target.style.boxShadow = '0 0 0 3px rgba(255, 105, 97, 0.1)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#E2E8F0';
                    e.target.style.background = '#F8FAFC';
                    e.target.style.boxShadow = 'none';
                  }}
                />
                <button
                  onClick={handleReply}
                  disabled={submittingReply || !replyMessage.trim()}
                  style={{
                    padding: '12px 24px',
                    background: (submittingReply || !replyMessage.trim())
                      ? '#CBD5E1'
                      : 'linear-gradient(135deg, #FF6961 0%, #FF8A80 100%)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: (submittingReply || !replyMessage.trim()) ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    transition: 'all 0.2s ease',
                    boxShadow: (submittingReply || !replyMessage.trim()) ? 'none' : '0 2px 8px rgba(255, 105, 97, 0.2)'
                  }}
                  onMouseEnter={(e) => {
                    if (!submittingReply && replyMessage.trim()) {
                      e.currentTarget.style.transform = 'translateY(-1px)';
                      e.currentTarget.style.boxShadow = '0 4px 12px rgba(255, 105, 97, 0.3)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!submittingReply && replyMessage.trim()) {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 2px 8px rgba(255, 105, 97, 0.2)';
                    }
                  }}
                >
                  {submittingReply ? (
                    <>
                      <span className="spinner" style={{
                        width: '16px',
                        height: '16px',
                        border: '2px solid rgba(255,255,255,0.3)',
                        borderTopColor: 'white',
                        borderRadius: '50%',
                        animation: 'spin 0.6s linear infinite'
                      }}></span>
                      <span>Sending...</span>
                    </>
                  ) : (
                    <>
                      <span>📤</span>
                      <span>Send Reply</span>
                    </>
                  )}
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default HelpSupport;
